import {
  normalizeScheduleCellCode,
  resolveShiftFromScheduleCodeFromMaps,
  type ShiftLookupMaps,
} from "@/utils/shift-util";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";

/**
 * 원티드 반영 여부 판정 — 셀·행·요약 바가 같은 답을 내도록 여기 한 곳에서만 센다.
 *
 * 고정 원티드와 금지근무는 판정 방향이 반대다.
 *   고정: 요청한 코드가 **들어갔는가**   → 다르면 문제(미반영)
 *   금지: 금지한 코드가 **안 들어갔는가** → 같으면 문제(위반)
 *
 * 껐음(is_applied=false)은 지킬 의무가 없으므로 분모에서도 뺀다.
 */

export type BannedOverlayEntry = {
  shiftIds: string[];
  isApplied: boolean;
  source?: string;
  reason?: string | null;
};

/** useRosterWantedOverlay 가 간호사마다 얹어 주는 맵들. */
export type WantedComplianceOverlay = {
  wantedEntriesMap?: Record<number, string>;
  isAppliedMap?: Record<number, boolean>;
  bannedShiftMap?: Record<number, BannedOverlayEntry>;
};

export type WantedComplianceDetail = {
  day: number;
  text: string;
  bad: boolean;
};

export type WantedComplianceCounts = {
  /** 살아 있는(켜진) 고정 원티드 건수 */
  wantedOn: number;
  wantedMissed: number;
  /** 살아 있는(켜진) 금지근무 건수 */
  bannedOn: number;
  bannedViolated: number;
};

export type NurseWantedCompliance = WantedComplianceCounts & {
  wantedDetails: WantedComplianceDetail[];
  bannedDetails: WantedComplianceDetail[];
};

const EMPTY_COUNTS: WantedComplianceCounts = {
  wantedOn: 0,
  wantedMissed: 0,
  bannedOn: 0,
  bannedViolated: 0,
};

/** 근무표 셀 값 → 표준 shift_id. 빈 칸과 "-" 는 미배정으로 본다. */
function cellShiftId(
  raw: unknown,
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift>
): string {
  const code = normalizeScheduleCellCode(raw as never).trim();
  if (!code || code === "-") return "";
  return (
    resolveShiftFromScheduleCodeFromMaps(code, shiftLookupMaps)?.shift_id ?? code
  );
}

function toShiftId(
  code: string,
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift>
): string {
  return (
    resolveShiftFromScheduleCodeFromMaps(code, shiftLookupMaps)?.shift_id ?? code
  );
}

/**
 * 간호사 1명의 원티드 준수 현황.
 *
 * @param overlay useRosterWantedOverlay 가 얹은 맵들
 * @param schedule 1일이 index 0 인 월간 근무 배열
 */
export function summarizeNurseWantedCompliance(
  overlay: WantedComplianceOverlay,
  schedule: readonly unknown[] | null | undefined,
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift>
): NurseWantedCompliance {
  const bannedMap = overlay.bannedShiftMap ?? {};
  const wantedMap = overlay.wantedEntriesMap ?? {};
  const appliedMap = overlay.isAppliedMap ?? {};

  const bannedDetails: WantedComplianceDetail[] = [];
  const wantedDetails: WantedComplianceDetail[] = [];
  let bannedOn = 0;
  let bannedViolated = 0;
  let wantedOn = 0;
  let wantedMissed = 0;

  Object.entries(bannedMap).forEach(([rawDay, info]) => {
    if (!info?.isApplied) return;
    const day = Number(rawDay);
    bannedOn += 1;
    const actual = cellShiftId(schedule?.[day - 1], shiftLookupMaps);
    const codes = (info.shiftIds ?? []).join(",");
    const violated =
      !!actual &&
      (info.shiftIds ?? []).some(
        (id) => toShiftId(id, shiftLookupMaps) === actual
      );
    if (violated) bannedViolated += 1;
    bannedDetails.push({
      day,
      text: `${String(day).padStart(2, " ")}일  금지 ${codes} → ${actual || "-"}  ${
        violated ? "★위반" : "준수"
      }`,
      bad: violated,
    });
  });

  Object.entries(wantedMap).forEach(([rawDay, code]) => {
    const day = Number(rawDay);
    if (appliedMap[day] === false) return;
    wantedOn += 1;
    const actual = cellShiftId(schedule?.[day - 1], shiftLookupMaps);
    const want = toShiftId(code, shiftLookupMaps);
    const missed = !!actual && actual !== want;
    if (missed) wantedMissed += 1;
    wantedDetails.push({
      day,
      text: `${String(day).padStart(2, " ")}일  요청 ${want} → ${actual || "-"}  ${
        missed ? "★미반영" : "반영"
      }`,
      bad: missed,
    });
  });

  return {
    wantedOn,
    wantedMissed,
    bannedOn,
    bannedViolated,
    wantedDetails,
    bannedDetails,
  };
}

/**
 * 호버 안내 문자열. 집계만 있으면 "그래서 며칠이 문제냐"를 다시 훑어야 한다.
 * 문제를 위로, 그다음 날짜순. 길면 자르되 **잘린 사실을 알린다**.
 */
export function buildWantedComplianceTitle(
  compliance: NurseWantedCompliance,
  limit = 12
): string {
  const pack = (list: WantedComplianceDetail[]) => {
    const sorted = [...list].sort(
      (a, b) => Number(b.bad) - Number(a.bad) || a.day - b.day
    );
    const head = sorted.slice(0, limit).map((x) => x.text);
    if (sorted.length > limit) head.push(`… 외 ${sorted.length - limit}건`);
    return head;
  };

  const lines: string[] = [];
  if (compliance.wantedOn) {
    lines.push(
      `원티드 ${compliance.wantedOn}건 · 반영 ${
        compliance.wantedOn - compliance.wantedMissed
      } · 미반영 ${compliance.wantedMissed}`,
      ...pack(compliance.wantedDetails)
    );
  }
  if (compliance.bannedOn) {
    if (lines.length) lines.push("");
    lines.push(
      `금지 ${compliance.bannedOn}건 · 준수 ${
        compliance.bannedOn - compliance.bannedViolated
      } · 위반 ${compliance.bannedViolated}`,
      ...pack(compliance.bannedDetails)
    );
  }
  return lines.join("\n");
}

export type WantedComplianceTotals = WantedComplianceCounts & {
  /** 미반영이 1건이라도 있는 사람 이름 — 요약에서 바로 누구인지 알려 준다. */
  wantedMissedNames: string[];
  /** 위반이 1건이라도 있는 사람 이름 */
  bannedViolatedNames: string[];
};

type NurseLike = WantedComplianceOverlay & {
  name?: string | null;
  schedule?: readonly unknown[];
};

/**
 * 그룹 전체 합계. 토글을 켜기 전에도 "금지가 걸려 있는지" 알 수 있어야 해서
 * 셀 표시와 무관하게 따로 센다.
 */
export function aggregateWantedCompliance(
  nurses: readonly NurseLike[] | undefined,
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift> | undefined
): WantedComplianceTotals {
  const empty: WantedComplianceTotals = {
    ...EMPTY_COUNTS,
    wantedMissedNames: [],
    bannedViolatedNames: [],
  };
  if (!nurses?.length || !shiftLookupMaps) return empty;

  return nurses.reduce<WantedComplianceTotals>((acc, nurse) => {
    const c = summarizeNurseWantedCompliance(
      nurse,
      nurse.schedule,
      shiftLookupMaps
    );
    acc.wantedOn += c.wantedOn;
    acc.wantedMissed += c.wantedMissed;
    acc.bannedOn += c.bannedOn;
    acc.bannedViolated += c.bannedViolated;
    if (c.wantedMissed > 0 && nurse.name) acc.wantedMissedNames.push(nurse.name);
    if (c.bannedViolated > 0 && nurse.name) {
      acc.bannedViolatedNames.push(nurse.name);
    }
    return acc;
  }, empty);
}
